/**
 * Keys that appear in a simulation's contentData but were never typed, chosen or uploaded by
 * the user in a use-case form.
 *
 * Two distinct reasons a key lands here, kept as separate sets because they are hidden for
 * different reasons and drift independently:
 *
 * - PLUMBING_KEYS: request wiring (audience/segment targeting, model routing, derived
 *   descriptions, backend defaults). Never user-authored content.
 * - RICH_RENDER_KEYS: genuinely user-authored, but already drawn by a dedicated block in
 *   SimulationInputsDropdown (image grids, question lists, concept cards). Listing them again
 *   as raw key/value would double-display them.
 *
 * Anything NOT in either set and NOT in the per-use-case registry still surfaces, under
 * "Other inputs" — a new form field can never be silently withheld from a rater.
 */

import { ENVELOPE_KEYS } from './useCaseInputRegistry';

export const PLUMBING_KEYS: ReadonlySet<string> = new Set([
  // Audience / segment targeting — chosen in SegmentsSelector, not in the use-case form.
  'audience_id',
  'audience_name',
  'segment_ids',
  'segments',
  'segment_filter',
  'segment_names',
  'persona_filters',
  // Resolved persona list — hundreds of numeric ids, attached during dispatch rather than typed
  // by the author. Surfaced by the "Other inputs" safety net on a real survey_and_focus_groups
  // simulation during rating-view verification, which is precisely what that lane is for.
  'persona_ids',
  'dynamic_segmentation_id',
  'dynamic_segment_indices',
  'dynamic_segment_names',
  'population',
  'sample_size',

  // Dispatch / routing.
  'task',
  'llm_model',
  'model_used',
  'source_model',
  'use_parsed_questions',
  'is_deep_simulation',
  'competitor_analysis',

  // Derived server-side or templated by the form rather than authored by the user.
  'description',
  'message_to_test',
  'products_data',
]);

/**
 * Rendered by their own dedicated blocks further down the inputs panel.
 *
 * The nested envelope containers are folded in from ENVELOPE_KEYS rather than listed by hand:
 * their children are resolved individually, so showing the container as well would dump the
 * same values a second time as raw JSON — and for an image-bearing envelope, a base64 data URI
 * as text. Deriving it means a new envelope cannot reintroduce that.
 */
export const RICH_RENDER_KEYS: ReadonlySet<string> = new Set([
  ...Object.values(ENVELOPE_KEYS),
  'claims',
  'images',
  'image_descriptions',
  'option_images',
  'current_logo',
  'questions',
  'concepts',
  'hero_image',
  'front_image',
  'side_images',
  'back_images',
  'competitor_images',
  'variant_images',
]);

export const isHiddenInputKey = (key: string): boolean =>
  PLUMBING_KEYS.has(key) || RICH_RENDER_KEYS.has(key);
